import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    CircularProgress,
    Paper,
    Button,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    TextField,
    MenuItem,
    Tabs,
    Tab,
    Divider,
    Switch,
    FormControlLabel,
} from '@mui/material';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import useMediaQuery from '@mui/material/useMediaQuery';
import { getAdminUsers, getUserById, inviteUser, updateUser, deleteUser, resendInvite } from '../../../utils/api';
import { getCurrentUser } from '../../../utils/session';
import { createNotification } from '../../../utils/alerts';
import { getErrorMessage } from '../../../utils/errorUtils';
import AdminFilters from '../../../components/admin/AdminFilters';
import ResponsiveAdminPage from '../../../components/admin/ResponsiveAdminPage';
import ResponsiveDataView from '../../../components/admin/ResponsiveDataView';

// ── Paleta ────────────────────────────────────────────────────────────────────
const PRIMARY      = '#f25d27';
const PRIMARY_DARK = '#d44e1f';

const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleString('pt-BR', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
    });
};

const fieldSx = {
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: PRIMARY },
    '& .MuiInputLabel-root.Mui-focused': { color: PRIMARY },
};

const CompactMetaField = ({ label, value }) => (
    <Typography sx={{ fontSize: '0.74rem', color: '#555', lineHeight: 1.3 }}>
        <Box component="span" sx={{ fontWeight: 700, color: '#8a8a8a' }}>
            {label}:
        </Box>{' '}
        {value || '—'}
    </Typography>
);

// Badge laranja/cinza usado para os indicadores de "Ativo" e "Administrador",
// visíveis apenas para usuários com is_superuser = true.
const StatusBadge = ({ active, activeLabel, inactiveLabel }) => (
    <Chip
        label={active ? activeLabel : inactiveLabel}
        size="small"
        sx={{
            backgroundColor: active ? PRIMARY : '#9e9e9e',
            color: '#fff',
            fontWeight: 600,
            fontSize: '0.7rem',
            height: 22,
        }}
    />
);

const UsersCompactCard = ({ row, onClick, showRoleInfo }) => (
    <Paper
        elevation={0}
        onClick={onClick}
        sx={{
            p: 1.2,
            borderRadius: '8px',
            border: '1px solid #ececec',
            cursor: onClick ? 'pointer' : 'default',
            '&:hover': onClick ? { backgroundColor: `${PRIMARY}08` } : undefined,
        }}
    >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.65 }}>
            <CompactMetaField label="Nome" value={row.full_name || '—'} />
            <CompactMetaField label="E-mail" value={row.email || '—'} />
            {showRoleInfo && (
                <Box sx={{ display: 'flex', gap: 0.6, alignItems: 'center', flexWrap: 'wrap', my: 0.2 }}>
                    <StatusBadge active={row.is_superuser} activeLabel="Administrador" inactiveLabel="Usuário" />
                </Box>
            )}
            <CompactMetaField label="Data de criação" value={formatDate(row.created_at)} />
            <CompactMetaField label="Última atualização" value={formatDate(row.updated_at)} />
        </Box>
    </Paper>
);

const BASE_COLUMNS = [
    { id: 'full_name', field: 'full_name', label: 'Nome', minWidth: 150 },
    { id: 'email', field: 'email', label: 'E-mail', minWidth: 200 },
    {
        id: 'created_at',
        field: 'created_at',
        label: 'Data de criação',
        minWidth: 160,
        render: (row) => formatDate(row.created_at),
    },
    {
        id: 'updated_at',
        field: 'updated_at',
        label: 'Última atualização',
        minWidth: 170,
        render: (row) => formatDate(row.updated_at),
    },
];

// Coluna "Administrador" só aparece para superusuários, na aba Usuários
// (não faz sentido para convites pendentes). "is_active" não é exibido aqui:
// refere-se a usuários que ainda não concluíram o cadastro (convites).
const ROLE_COLUMNS = [
    {
        id: 'is_superuser',
        field: 'is_superuser',
        label: 'Administrador',
        minWidth: 140,
        render: (row) => <StatusBadge active={row.is_superuser} activeLabel="Administrador" inactiveLabel="Usuário" />,
    },
];

const buildColumns = (showRoleInfo) => {
    if (!showRoleInfo) return BASE_COLUMNS;
    const [nameColumn, emailColumn, ...rest] = BASE_COLUMNS;
    return [nameColumn, emailColumn, ...ROLE_COLUMNS, ...rest];
};

// ── Componente principal ──────────────────────────────────────────────────────
export default function AdminUsersPage() {
    const isMobile = useMediaQuery('(max-width:600px)');
    const isCardsView = useMediaQuery('(max-width:1024px)');

    // Usuário logado — define as permissões da tela (aba Convites, botão de
    // convite, colunas de papel/status e o que pode ser feito no modal de detalhes.
    const [currentUser] = useState(() => getCurrentUser());
    const isSuperuser = Boolean(currentUser?.is_superuser);

    const [tab, setTab] = useState(0);

    // listagem
    const [users, setUsers]   = useState([]);
    const [total, setTotal]   = useState(0);
    const [loading, setLoading] = useState(false);

    const [searchInput, setSearchInput]       = useState('');
    const [search, setSearch]                 = useState('');
    // Filtro "Administrador" — só exibido/aplicado para superusuários.
    // Valores: '' (Todos), 'true' (Administrador), 'false' (Usuário).
    const [superuserFilter, setSuperuserFilter] = useState('');
    const [orderBy, setOrderBy]               = useState('created_at');
    const [orderDirection, setOrderDirection] = useState('desc');
    const [page, setPage]       = useState(0);
    const [pageSize, setPageSize] = useState(10);

    // modal de convite
    const [inviteOpen, setInviteOpen]       = useState(false);
    const [inviteEmail, setInviteEmail]     = useState('');
    const [inviteLoading, setInviteLoading] = useState(false);

    // modal de detalhes
    const [detailUser, setDetailUser] = useState(null);
    const [editName, setEditName]     = useState('');
    const [editEmail, setEditEmail]   = useState('');
    const [editWritingInstructions, setEditWritingInstructions] = useState('');
    const [editIsSuperuser, setEditIsSuperuser]   = useState(false);

    // ações do modal
    const [detailLoading, setDetailLoading] = useState(false);
    const [updateLoading, setUpdateLoading] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [resendLoading, setResendLoading] = useState(false);
    const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

    // Inicializa os campos editáveis quando abre o modal
    useEffect(() => {
        if (detailUser) {
            setEditName(detailUser.full_name || '');
            setEditEmail(detailUser.email || '');
            setEditWritingInstructions(detailUser.writing_instructions || '');
            setEditIsSuperuser(detailUser.is_superuser ?? false);
        }
    }, [detailUser]);

    // Debounce da busca
    useEffect(() => {
        const timer = setTimeout(() => {
            setSearch(searchInput);
            setPage(0);
        }, 400);
        return () => clearTimeout(timer);
    }, [searchInput]);

    // Re-carrega quando muda aba ou parâmetros
    useEffect(() => {
        fetchUsers();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tab, page, pageSize, search, orderBy, orderDirection, superuserFilter]);

    // ── Fetch ─────────────────────────────────────────────────────────────────
    const fetchUsers = async () => {
        setLoading(true);
        try {
            const params = {
                order_by: orderBy,
                order_direction: orderDirection,
                page,
                page_size: pageSize,
            };
            if (search) params.search = search;
            if (tab === 1) params.active = false;
            if (isSuperuser && tab === 0 && superuserFilter !== '') {
                params.is_superuser = superuserFilter === 'true';
            }

            const data = await getAdminUsers(params);
            setUsers(data.users);
            setTotal(data.total);
        } catch (err) {
            if (err.response?.status !== 401) {
                createNotification('error', 'Erro ao carregar usuários.', isMobile);
            }
        } finally {
            setLoading(false);
        }
    };

    // ── Handlers ──────────────────────────────────────────────────────────────
    const handleTabChange = (_, newTab) => {
        setTab(newTab);
        setSearchInput('');
        setSearch('');
        setSuperuserFilter('');
        setOrderBy('created_at');
        setOrderDirection('desc');
        setPage(0);
    };

    const handleSort = (field) => {
        if (orderBy === field) {
            setOrderDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
        } else {
            setOrderBy(field);
            setOrderDirection('asc');
        }
        setPage(0);
    };

    // Abre o modal de detalhes. Na aba Usuários, busca os dados completos
    // (incluindo writing_instructions) via GET — exceto quando um usuário não
    // administrador está vendo os detalhes de outro usuário, caso em que os
    // dados da listagem já bastam (a modal fica somente leitura).
    const handleOpenDetail = (row) => {
        setDetailUser(row);
        if (tab !== 0) return;

        const isOwnAccount = currentUser && String(row.id) === String(currentUser.user_id);
        if (!isSuperuser && !isOwnAccount) return;

        setDetailLoading(true);
        getUserById(row.id)
            .then((data) => setDetailUser(data))
            .catch((err) => {
                if (err.response?.status !== 401) {
                    createNotification('error', getErrorMessage(err, 'Erro ao carregar dados do usuário.'), isMobile);
                }
            })
            .finally(() => setDetailLoading(false));
    };

    // Permissões do modal de detalhes de usuário, calculadas a partir do
    // usuário logado (is_superuser) e de quem está sendo visualizado.
    const isOwnAccountDetail = Boolean(
        detailUser && currentUser && String(detailUser.id) === String(currentUser.user_id)
    );
    const canEditUserFields = isSuperuser || isOwnAccountDetail;
    const canDeleteUser     = isSuperuser;
    const canUpdateUser     = isSuperuser || isOwnAccountDetail;

    const handleCloseDetail = () => {
        if (updateLoading || deleteLoading || resendLoading) return;
        setDetailUser(null);
        setConfirmDeleteOpen(false);
    };

    // Atualizar usuário
    const handleUpdateUser = async () => {
        setUpdateLoading(true);
        try {
            const payload = {
                full_name: editName || null,
                email: editEmail || null,
                writing_instructions: editWritingInstructions || null,
            };
            // Toggle de Administrador só existe (e só pode ser alterado)
            // quando quem está logado é superusuário.
            if (isSuperuser) {
                payload.is_superuser = editIsSuperuser;
            }
            await updateUser(detailUser.id, payload);
            createNotification('success', 'Usuário atualizado com sucesso!', isMobile);
            handleCloseDetail();
            fetchUsers();
        } catch (err) {
            if (err.response?.status !== 401) {
                createNotification('error', getErrorMessage(err, 'Erro ao atualizar usuário.'), isMobile);
            }
        } finally {
            setUpdateLoading(false);
        }
    };

    // Excluir usuário ou convite
    const handleDeleteUser = async () => {
        setDeleteLoading(true);
        try {
            await deleteUser(detailUser.id);
            createNotification('success',
                tab === 0 ? 'Usuário excluído com sucesso!' : 'Convite excluído com sucesso!',
                isMobile,
            );
            setConfirmDeleteOpen(false);
            setDetailUser(null);
            fetchUsers();
        } catch (err) {
            if (err.response?.status !== 401) {
                createNotification('error', getErrorMessage(err, 'Erro ao excluir.'), isMobile);
            }
        } finally {
            setDeleteLoading(false);
        }
    };

    // Reenviar convite
    const handleResendInvite = async () => {
        setResendLoading(true);
        try {
            await resendInvite(detailUser.id);
            createNotification('success', 'Convite reenviado com sucesso!', isMobile);
        } catch (err) {
            if (err.response?.status !== 401) {
                createNotification('error', getErrorMessage(err, 'Erro ao reenviar convite.'), isMobile);
            }
        } finally {
            setResendLoading(false);
        }
    };

    // Invite modal handlers
    const handleInviteOpen  = () => { setInviteEmail(''); setInviteOpen(true); };
    const handleInviteClose = () => { if (inviteLoading) return; setInviteOpen(false); };

    const handleInviteConfirm = async () => {
        setInviteLoading(true);
        try {
            await inviteUser({ email: inviteEmail });
            createNotification('success', 'Convite enviado com sucesso!', isMobile);
            setInviteOpen(false);
            if (tab === 1) {
                fetchUsers();
            } else {
                setTab(1);
                setSearchInput('');
                setSearch('');
                setSuperuserFilter('');
                setOrderBy('created_at');
                setOrderDirection('desc');
                setPage(0);
            }
        } catch (err) {
            if (err.response?.status !== 401) {
                createNotification('error', getErrorMessage(err, 'Erro ao enviar convite.'), isMobile);
            }
        } finally {
            setInviteLoading(false);
        }
    };

    // ── Render ────────────────────────────────────────────────────────────────
    const isUserTab   = tab === 0;
    const isInviteTab = tab === 1;

    return (
        <ResponsiveAdminPage>
            <Box sx={{ mb: 2 }}>
                <Typography variant="h5" sx={{ fontWeight: 700, color: '#1a1a1a', lineHeight: 1, mb: 0.4 }}>
                    Usuários
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: 1 }}>
                    <Typography variant="caption" sx={{ color: '#999', fontSize: '0.78rem' }}>
                        Gerencie usuários e convites
                    </Typography>
                    {isSuperuser && (
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                            <Button
                                variant="contained"
                                size="small"
                                startIcon={<PersonAddIcon fontSize="small" />}
                                onClick={handleInviteOpen}
                                sx={{
                                    backgroundColor: PRIMARY,
                                    '&:hover': { backgroundColor: PRIMARY_DARK },
                                    textTransform: 'none',
                                    boxShadow: 'none',
                                    fontWeight: 600,
                                }}
                            >
                                Convidar usuário
                            </Button>
                        </Box>
                    )}
                </Box>
            </Box>

            {/* Abas — "Convites" só é exibida para superusuários */}
            {isSuperuser && (
                <Tabs
                    value={tab}
                    onChange={handleTabChange}
                    sx={{
                        mb: 2,
                        '& .MuiTabs-indicator': { backgroundColor: PRIMARY },
                        '& .MuiTab-root': { textTransform: 'none', fontWeight: 500 },
                        '& .Mui-selected': { color: `${PRIMARY} !important` },
                    }}
                >
                    <Tab label="Usuários" />
                    <Tab label="Convites" />
                </Tabs>
            )}

            <AdminFilters search={searchInput} onSearchChange={setSearchInput}>
                {isSuperuser && isUserTab && (
                    <TextField
                        select
                        size="small"
                        label="Administrador"
                        value={superuserFilter}
                        onChange={(e) => { setSuperuserFilter(e.target.value); setPage(0); }}
                        sx={{
                            minWidth: 160,
                            '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: PRIMARY },
                            '& .MuiInputLabel-root.Mui-focused': { color: PRIMARY },
                        }}
                    >
                        <MenuItem value="">Todos</MenuItem>
                        <MenuItem value="true">Administrador</MenuItem>
                        <MenuItem value="false">Usuário</MenuItem>
                    </TextField>
                )}
            </AdminFilters>

            <Paper
                sx={{
                    borderRadius: '8px',
                    overflow: 'hidden',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                    p: isCardsView ? 1 : 0,
                }}
            >
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                        <CircularProgress sx={{ color: PRIMARY }} />
                    </Box>
                ) : (
                    <ResponsiveDataView
                        columns={buildColumns(isSuperuser && isUserTab)}
                        rows={users}
                        orderBy={orderBy}
                        orderDirection={orderDirection}
                        onSort={handleSort}
                        onRowClick={(row) => handleOpenDetail(row)}
                        mobileBreakpoint={1024}
                        mobileCardRenderer={(row, index) => (
                            <UsersCompactCard
                                key={row.id ?? index}
                                row={row}
                                onClick={() => handleOpenDetail(row)}
                                showRoleInfo={isSuperuser && isUserTab}
                            />
                        )}
                        emptyMessage={isUserTab ? 'Nenhum usuário encontrado.' : 'Nenhum convite encontrado.'}
                        page={page}
                        pageSize={pageSize}
                        total={total}
                        onPageChange={setPage}
                        onPageSizeChange={(size) => { setPageSize(size); setPage(0); }}
                        paperSx={{ borderRadius: '8px', border: 'none', boxShadow: 'none' }}
                    />
                )}
            </Paper>

            {/* ── Modal: Dados do usuário (aba Usuários) ────────────────── */}
            <Dialog
                open={Boolean(detailUser) && isUserTab}
                onClose={handleCloseDetail}
                fullWidth
                maxWidth="xs"
                PaperProps={{ sx: { borderRadius: '12px' } }}
            >
                <DialogTitle sx={{ fontWeight: 600, color: '#333' }}>
                    Dados do usuário
                </DialogTitle>

                <DialogContent sx={{ pt: '8px !important' }}>
                    {detailUser && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <TextField
                                label="Nome"
                                size="small"
                                fullWidth
                                value={editName}
                                onChange={(e) => setEditName(e.target.value)}
                                disabled={updateLoading || !canEditUserFields}
                                sx={fieldSx}
                            />
                            <TextField
                                label="E-mail"
                                type="email"
                                size="small"
                                fullWidth
                                value={editEmail}
                                onChange={(e) => setEditEmail(e.target.value)}
                                disabled={updateLoading || !canEditUserFields}
                                sx={fieldSx}
                            />
                            <TextField
                                label="Instruções de escrita"
                                size="small"
                                fullWidth
                                multiline
                                minRows={3}
                                maxRows={8}
                                value={editWritingInstructions}
                                onChange={(e) => setEditWritingInstructions(e.target.value)}
                                disabled={updateLoading || detailLoading || !canEditUserFields}
                                sx={fieldSx}
                            />

                            {isSuperuser && (
                                <>
                                    <Divider />
                                    <Box sx={{ display: 'flex', gap: 3 }}>
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={editIsSuperuser}
                                                    onChange={(e) => setEditIsSuperuser(e.target.checked)}
                                                    disabled={updateLoading}
                                                    sx={{
                                                        '& .MuiSwitch-switchBase.Mui-checked': { color: PRIMARY },
                                                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: PRIMARY },
                                                    }}
                                                />
                                            }
                                            label="Administrador"
                                        />
                                    </Box>
                                </>
                            )}

                            <Divider />

                            <Box sx={{ display: 'flex', gap: 3 }}>
                                <Box>
                                    <Typography variant="caption" sx={{ color: '#888', display: 'block', mb: 0.5 }}>
                                        Data de criação
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: '#333' }}>
                                        {formatDate(detailUser.created_at)}
                                    </Typography>
                                </Box>
                                {detailUser.updated_at && (
                                    <Box>
                                        <Typography variant="caption" sx={{ color: '#888', display: 'block', mb: 0.5 }}>
                                            Última atualização
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: '#333' }}>
                                            {formatDate(detailUser.updated_at)}
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                        </Box>
                    )}
                </DialogContent>

                <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
                    {/* Excluir à esquerda — apenas superusuários */}
                    {canDeleteUser && (
                        <Button
                            onClick={() => setConfirmDeleteOpen(true)}
                            disabled={updateLoading || deleteLoading}
                            sx={{
                                color: '#d32f2f', textTransform: 'none',
                                '&:hover': { backgroundColor: 'rgba(211,47,47,0.06)' },
                            }}
                        >
                            Excluir
                        </Button>
                    )}
                    <Box sx={{ flex: 1 }} />
                    <Button
                        onClick={handleCloseDetail}
                        disabled={updateLoading}
                        sx={{ color: '#666', textTransform: 'none' }}
                    >
                        Fechar
                    </Button>
                    {canUpdateUser && (
                        <Button
                            variant="contained"
                            onClick={handleUpdateUser}
                            disabled={updateLoading || detailLoading || !editEmail}
                            sx={{ backgroundColor: PRIMARY, '&:hover': { backgroundColor: PRIMARY_DARK }, textTransform: 'none', boxShadow: 'none', borderRadius: '8px' }}
                        >
                            {updateLoading ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'Atualizar'}
                        </Button>
                    )}
                </DialogActions>
            </Dialog>

            {/* ── Modal: Dados do convite (aba Convites) ────────────────── */}
            <Dialog
                open={Boolean(detailUser) && isInviteTab}
                onClose={handleCloseDetail}
                fullWidth
                maxWidth="xs"
                PaperProps={{ sx: { borderRadius: '12px' } }}
            >
                <DialogTitle sx={{ fontWeight: 600, color: '#333' }}>
                    Dados do convite
                </DialogTitle>

                <DialogContent sx={{ pt: '8px !important' }}>
                    {detailUser && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <Box>
                                <Typography variant="caption" sx={{ color: '#888', display: 'block', mb: 0.5 }}>
                                    Nome
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#333', fontWeight: 500 }}>
                                    {detailUser.full_name || '—'}
                                </Typography>
                            </Box>

                            <Divider />

                            <Box>
                                <Typography variant="caption" sx={{ color: '#888', display: 'block', mb: 0.5 }}>
                                    E-mail
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#333' }}>
                                    {detailUser.email}
                                </Typography>
                            </Box>

                            <Divider />

                            <Box>
                                <Typography variant="caption" sx={{ color: '#888', display: 'block', mb: 0.5 }}>
                                    Data de criação
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#333' }}>
                                    {formatDate(detailUser.created_at)}
                                </Typography>
                            </Box>
                        </Box>
                    )}
                </DialogContent>

                <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
                    {/* Excluir à esquerda */}
                    <Button
                        onClick={() => setConfirmDeleteOpen(true)}
                        disabled={resendLoading || deleteLoading}
                        sx={{
                            color: '#d32f2f', textTransform: 'none',
                            '&:hover': { backgroundColor: 'rgba(211,47,47,0.06)' },
                        }}
                    >
                        Excluir
                    </Button>
                    <Box sx={{ flex: 1 }} />
                    <Button
                        onClick={handleCloseDetail}
                        disabled={resendLoading}
                        sx={{ color: '#666', textTransform: 'none' }}
                    >
                        Fechar
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleResendInvite}
                        disabled={resendLoading}
                        sx={{ backgroundColor: PRIMARY, '&:hover': { backgroundColor: PRIMARY_DARK }, textTransform: 'none', boxShadow: 'none', borderRadius: '8px' }}
                    >
                        {resendLoading ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'Reenviar'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ── Modal: Confirmação de exclusão ────────────────────────── */}
            <Dialog
                open={confirmDeleteOpen}
                onClose={() => !deleteLoading && setConfirmDeleteOpen(false)}
                maxWidth="xs"
                fullWidth
                PaperProps={{ sx: { borderRadius: '12px' } }}
            >
                <DialogTitle sx={{ fontWeight: 600, color: '#333' }}>
                    Confirmar exclusão
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Deseja realmente excluir {isUserTab ? 'o usuário' : 'o convite'} <strong>{detailUser?.email}</strong>?
                        {isUserTab && ' Esta ação não poderá ser desfeita.'}
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
                    <Button
                        onClick={() => setConfirmDeleteOpen(false)}
                        disabled={deleteLoading}
                        sx={{ color: '#666', textTransform: 'none' }}
                    >
                        Cancelar
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleDeleteUser}
                        disabled={deleteLoading}
                        sx={{
                            backgroundColor: '#d32f2f', textTransform: 'none', boxShadow: 'none', borderRadius: '8px',
                            '&:hover': { backgroundColor: '#b71c1c' },
                        }}
                    >
                        {deleteLoading ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'Excluir'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ── Modal: Novo convite ───────────────────────────────────── */}
            <Dialog
                open={inviteOpen}
                onClose={handleInviteClose}
                fullWidth
                maxWidth="xs"
                PaperProps={{ sx: { borderRadius: '12px' } }}
            >
                <DialogTitle sx={{ fontWeight: 600, color: '#333' }}>
                    Convidar usuário
                </DialogTitle>
                <DialogContent>
                    <TextField
                        label="E-mail"
                        type="email"
                        value={inviteEmail}
                        onChange={(e) => setInviteEmail(e.target.value)}
                        fullWidth
                        autoFocus
                        disabled={inviteLoading}
                        sx={{ mt: 1, ...fieldSx }}
                        onKeyDown={(e) => { if (e.key === 'Enter' && inviteEmail) handleInviteConfirm(); }}
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
                    <Button onClick={handleInviteClose} disabled={inviteLoading} sx={{ color: '#666', textTransform: 'none' }}>
                        Cancelar
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleInviteConfirm}
                        disabled={!inviteEmail || inviteLoading}
                        sx={{ backgroundColor: PRIMARY, '&:hover': { backgroundColor: PRIMARY_DARK }, textTransform: 'none', boxShadow: 'none', borderRadius: '8px' }}
                    >
                        {inviteLoading ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'Confirmar'}
                    </Button>
                </DialogActions>
            </Dialog>
        </ResponsiveAdminPage>
    );
}
